import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Space, InputNumber, message, Modal, Table, Alert } from 'antd';
import { MinusCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { WordPressAPI } from '../../services/api/api';
import { WCPalletLayout, WCPallet, WCProduct } from '../../types/wordpress';
import type { ColumnsType } from 'antd/es/table';


interface PalletLayoutFormProps {
    layout?: WCPalletLayout;
    onSave: (values: Partial<WCPalletLayout>) => void;
    onCancel: () => void;
}

const PalletLayoutForm: React.FC<PalletLayoutFormProps> = ({
    layout,
    onSave,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [pallets, setPallets] = useState<WCPallet[]>([]);
    const [products, setProducts] = useState<WCProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState<'barcode' | 'name' | 'sku' | 'fda'>('name');
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [totalPercentage, setTotalPercentage] = useState(0);

    const calculateTotalPercentage = (products: any[]) => {
        const total = products?.reduce((sum, product) => sum + (product?.percentage || 0), 0) || 0;
        setTotalPercentage(total);
        return total;
    };

    useEffect(() => {
        const products = form.getFieldValue('products') || [];
        calculateTotalPercentage(products);

        form.setFields([{ name: 'products', value: products }]);
    }, [form.getFieldValue('products')]);

    const getPercentageStatus = () => {
        if (totalPercentage === 100) return 'success';
        if (totalPercentage > 100) return 'error';
        return 'warning';
    };

    const handleSearch = async (value: string) => {
        if (!value || value.length < 3) return;

        setLoading(true);
        try {
            const response = await WordPressAPI.searchProducts(value, searchType);
            if (Array.isArray(response)) {
                setProducts(response);
                setIsModalVisible(true);
            }
        } catch (error) {
            message.error('Failed to search products');
        } finally {
            setLoading(false);
        }
    };

    const handleAddProduct = (product: WCProduct) => {
        const products = form.getFieldValue('products') || [];
        form.setFieldsValue({
            products: [...products, {
                product_id: product.id,
                quantity: 1,
                percentage: 0
            }]
        });
        setIsModalVisible(false);
        setSearchText('');
    };

    const searchResultColumns: ColumnsType<WCProduct> = [
        {
            title: 'Name',
            dataIndex: ['title', 'rendered'],
            key: 'name',
            render: (_, record) => record.name
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddProduct(record)}
                >
                    Add
                </Button>
            ),
        },
    ];

    useEffect(() => {
        fetchPallets();
        fetchProducts();
        if (layout) {
            form.setFieldsValue({
                title: layout.title.rendered,
                pallet_id: layout.meta.pallet_id,
                products: layout.meta.products,
            });
        }
    }, [layout]);

    const fetchPallets = async () => {
        try {
            const response = await WordPressAPI.getPallets({});
            setPallets(response.items);
        } catch (error) {
            message.error('Failed to fetch pallets');
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await WordPressAPI.getProducts({});
            setProducts(response.items);
        } catch (error) {
            message.error('Failed to fetch products');
        }
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        let newValue = {};
        try {
            if (layout) {
                newValue = {
                    title: values.title,
                    meta: {
                        pallet_id: values.pallet_id,
                        products: values.products,
                    },
                }
            } else {
                newValue = {
                    title: values.title,
                    meta: {
                        pallet_id: values.pallet_id,
                        products: values.products,
                    },
                }
            }

            onSave(newValue);
        } catch (error) {
            message.error(`Failed to ${layout ? 'update' : 'create'} layout`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ products: [{}] }}
        >
            <Form.Item
                name="title"
                label="Layout Title"
                rules={[{ required: true }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name="pallet_id"
                label="Pallet"
                rules={[{ required: true }]}
            >
                <Select>
                    {pallets.map(pallet => (
                        <Select.Option key={pallet.id} value={pallet.id}>
                            {pallet.title.rendered}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.List name="products">
                {(fields, { add, remove }) => (
                    <>
                        <Alert
                            message={`Total Percentage: ${totalPercentage}%`}
                            type={getPercentageStatus()}
                            showIcon
                            description={
                                totalPercentage !== 100
                                    ? `The total percentage should be 100%. Current total is ${totalPercentage}%`
                                    : 'Perfect! The total percentage is 100%'
                            }
                            style={{ marginBottom: 16 }}
                        />

                        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                            <Select
                                value={searchType}
                                onChange={(value) => setSearchType(value)}
                                style={{ width: 120 }}
                            >
                                <Select.Option value="name">Name</Select.Option>
                                <Select.Option value="sku">SKU</Select.Option>
                                <Select.Option value="barcode">Barcode</Select.Option>
                                <Select.Option value="fda">FDA</Select.Option>
                            </Select>
                            <Input
                                placeholder={`Search by ${searchType}`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onPressEnter={() => handleSearch(searchText)}
                            />
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={() => handleSearch(searchText)}
                            >
                                Search
                            </Button>
                        </Space.Compact>

                        {fields.map(({ key, name, ...restField }) => (
                            <Space key={key} align="baseline">
                                <Form.Item
                                    {...restField}
                                    name={[name, 'product_id']}
                                    rules={[{ required: true }]}
                                >
                                    <Select style={{ width: 200 }} placeholder="Select product">
                                        {products.map(product => (
                                            <Select.Option key={product.id} value={product.id}>
                                                {product.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    {...restField}
                                    name={[name, 'quantity']}
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber min={1} placeholder="Quantity" />
                                </Form.Item>

                                <Form.Item
                                    {...restField}
                                    name={[name, 'percentage']}
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        placeholder="Percentage"
                                        formatter={value => `${value}%`}
                                        parser={(value: string | undefined): 0 | 100 => {
                                            const num = parseFloat(value?.replace('%', '') || '0');
                                            return (num > 100 ? 100 : num < 0 ? 0 : num) as 0 | 100;
                                        }}
                                    />
                                </Form.Item>

                                <MinusCircleOutlined onClick={() => remove(name)} />
                            </Space>
                        ))}
                    </>
                )}
            </Form.List>

            <Modal
                title="Search Products"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSearchText('');
                    setProducts([]);
                }}
                footer={null}
                width={800}
            >
                <Table
                    columns={searchResultColumns}
                    dataSource={products}
                    loading={loading}
                    rowKey="name"
                    pagination={false}
                />
            </Modal>
            {/* <Form.List name="products">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                            <Space key={key} align="baseline">
                                <Form.Item
                                    {...restField}
                                    name={[name, 'product_id']}
                                    rules={[{ required: true }]}
                                >
                                    <Select style={{ width: 200 }} placeholder="Select product">
                                        {products.map(product => (
                                            <Select.Option key={product.id} value={product.id}>
                                                {product.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    {...restField}
                                    name={[name, 'quantity']}
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber min={1} placeholder="Quantity" />
                                </Form.Item>

                                <Form.Item
                                    {...restField}
                                    name={[name, 'percentage']}
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        placeholder="Percentage"
                                        formatter={value => `${value}%`}
                                        parser={(value: string | undefined): 0 | 100 => {
                                            const num = parseFloat(value?.replace('%', '') || '0');
                                            return (num > 100 ? 100 : num < 0 ? 0 : num) as 0 | 100;
                                        }}
                                    />
                                </Form.Item>

                                <MinusCircleOutlined onClick={() => remove(name)} />
                            </Space>
                        ))}

                        <Form.Item>
                            <Button
                                type="dashed"
                                onClick={() => add()}
                                block
                                icon={<PlusOutlined />}
                            >
                                Add Product
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List> */}

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {layout ? 'Update' : 'Create'} Layout
                    </Button>
                    <Button onClick={onCancel}>Cancel</Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default PalletLayoutForm; 